import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { loadGames, loadEligibility, searchGames, ineligibleReason, ID, TITLE, CONSOLE, IS_HACK, HOURS } from '../util/gameFeed';

/**
 * Forum equivalent of Discord's /nominate, following the same three steps:
 * pick the event, search for the game, confirm.
 *
 * Ineligible games are SHOWN with the reason rather than hidden, matching
 * the comment in cogs/nominate.py: "Silently omitting a game someone
 * specifically searched for reads as the bot being broken."
 *
 * No rule is evaluated here. The event filter reads a precomputed isHack
 * flag, and ineligibility is a dictionary lookup - both decided in Python
 * by the same functions Discord uses, so the two platforms cannot drift
 * into disagreeing about what is nominatable.
 *
 * Play time is displayed, never used to filter, because Discord's
 * autocomplete does not consult it either - hours are checked after
 * selection, and a game with none cached goes to admin approval exactly as
 * it does today.
 */
export default class NominateModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.mode = 'nominate'; // or 'cancel'
    this.event = 'gotm';
    this.query = '';
    this.games = null;
    this.eligibility = {};
    this.loading = true;
    this.error = null;
    this.picked = null;
    this.load();
  }
  async load() {
    this.loading = true;
    m.redraw();
    try {
      const _await$Promise$all = await Promise.all([loadGames(), loadEligibility(this.event)]),
        _await$Promise$all2 = _slicedToArray(_await$Promise$all, 2),
        games = _await$Promise$all2[0],
        eligibility = _await$Promise$all2[1];
      this.games = games;
      this.eligibility = eligibility;
    } catch (e) {
      // Falls back to the manual URL box rather than blocking - a member
      // must still be able to nominate if the feed is missing.
      this.games = null;
    }
    this.loading = false;
    m.redraw();
  }
  className() {
    return 'jrh-CommandModal jrh-NominateModal Modal--medium';
  }
  title() {
    return app.translator.trans('jrh-forum-commands.forum.nominate.title');
  }
  content() {
    if (this.picked) return this.confirmView();
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form-group jrh-CommandModal-modes"
    }, m(Button, {
      className: 'Button ' + (this.mode === 'nominate' ? 'Button--primary' : ''),
      onclick: () => {
        this.mode = 'nominate';
        this.error = null;
      }
    }, app.translator.trans('jrh-forum-commands.forum.nominate.mode_nominate')), m(Button, {
      className: 'Button ' + (this.mode === 'cancel' ? 'Button--primary' : ''),
      onclick: () => {
        this.mode = 'cancel';
        this.error = null;
      }
    }, app.translator.trans('jrh-forum-commands.forum.nominate.mode_cancel'))), m("p", {
      className: "helpText"
    }, this.mode === 'cancel' ? app.translator.trans('jrh-forum-commands.forum.nominate.cancel_help') : app.translator.trans('jrh-forum-commands.forum.nominate.help')), this.mode === 'nominate' ? m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.nominate.event_label')), m(Select, {
      options: {
        gotm: 'Game of the Month',
        rhotm: 'Romhack of the Month',
        gots: 'Game of the Season'
      },
      value: this.event,
      onchange: value => {
        this.event = value;
        this.error = null;
        // Eligibility is per event, so it must be refetched - the
        // same game can be blocked for one event and free for another.
        this.load();
      }
    })) : null, this.mode === 'cancel' ? this.cancelView() : this.searchView());
  }
  searchView() {
    if (this.loading) return m(LoadingIndicator, null);

    // No feed published yet, or it failed to load: fall back to the manual
    // URL box so nominating is never blocked by this UI.
    if (!this.games) return this.manualView();
    const results = searchGames(this.games, this.query, this.event);
    return [m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.nominate.search_label')), m("input", {
      className: "FormControl",
      type: "text",
      autocomplete: "off",
      placeholder: app.translator.trans('jrh-forum-commands.forum.nominate.search_placeholder'),
      value: this.query,
      oninput: e => {
        this.query = e.target.value;
      }
    })), m("ul", {
      className: "jrh-GameList"
    }, results.map(game => {
      const reason = ineligibleReason(game, this.eligibility);
      const hours = game[HOURS];
      return m("li", {
        className: 'jrh-GameList-item' + (reason ? ' jrh-GameList-item--blocked' : ''),
        onclick: () => {
          // Blocked games are deliberately still listed, but picking
          // one would only produce a nomination the bot rejects.
          if (!reason) this.picked = game;
        }
      }, m("span", {
        className: "jrh-GameList-title"
      }, reason ? '⛔ ' : '', game[TITLE]), m("span", {
        className: "jrh-GameList-meta"
      }, reason || [game[CONSOLE], hours ? "".concat(hours, "h") : null].filter(Boolean).join(' • ')));
    }), results.length === 0 ? m("li", {
      className: "jrh-GameList-empty"
    }, app.translator.trans('jrh-forum-commands.forum.nominate.no_results')) : null)];
  }

  /**
   * The member's own nominations, each with a cancel button.
   *
   * Read from the clubActivity blob the bot writes to every profile - the
   * public eligibility feed lists which TITLES are taken but deliberately
   * not who nominated them, so it cannot answer "what did I nominate?".
   *
   * Every reason a cancel would be refused is shown up front rather than
   * discovered by clicking, because cancelling SPENDS the member's one
   * change for the period: asking someone to commit to that before telling
   * them what it costs is the wrong way round.
   */
  cancelView() {
    var _activity;
    let activity = null;
    try {
      var _app$session$user;
      activity = JSON.parse(((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.attribute('clubActivity')) || 'null');
    } catch (e) {
      activity = null;
    }
    const events = (((_activity = activity) == null ? void 0 : _activity.events) || []).filter(e => e.event !== 'gotw');
    const mine = events.filter(e => e.nomination);
    if (!events.length) {
      // No blob yet - the bot writes it within a minute of a nomination, so
      // this is the small window right after nominating, not an error.
      return m("p", {
        className: "helpText"
      }, app.translator.trans('jrh-forum-commands.forum.nominate.cancel_unknown'));
    }
    if (!mine.length) {
      return m("p", {
        className: "helpText"
      }, app.translator.trans('jrh-forum-commands.forum.nominate.cancel_none'));
    }
    return m("ul", {
      className: "jrh-GameList jrh-CancelList"
    }, mine.map(entry => {
      const nom = entry.nomination;
      // Mirrors the two checks cancel_nomination itself enforces, in the
      // same order, so the dialog and the bot never disagree.
      const blocked = nom.in_poll ? app.translator.trans('jrh-forum-commands.forum.nominate.cancel_in_poll') : nom.change_used ? app.translator.trans('jrh-forum-commands.forum.nominate.cancel_change_used') : null;
      return m("li", {
        className: 'jrh-GameList-item' + (blocked ? ' jrh-GameList-item--blocked' : '')
      }, m("span", {
        className: "jrh-GameList-title"
      }, entry.label), m("span", {
        className: "jrh-GameList-meta"
      }, nom.title), blocked ? m("span", {
        className: "jrh-GameList-meta jrh-CancelList-blocked"
      }, "\u26D4 ", blocked) : m(Button, {
        className: "Button Button--primary jrh-CancelList-button",
        onclick: () => this.insertCancel(entry.event)
      }, app.translator.trans('jrh-forum-commands.forum.nominate.cancel_this')));
    }));
  }
  confirmView() {
    const game = this.picked;
    const hours = game[HOURS];
    // Same wording Discord uses when it cannot verify play time, because
    // the outcome is the same: the bot routes it to admin approval.
    const hoursLabel = hours ? "".concat(hours, "h") : this.event === 'rhotm' ? null : app.translator.trans('jrh-forum-commands.forum.nominate.hours_unverified');
    return m("div", {
      className: "Modal-body"
    }, m("h3", {
      className: "jrh-Confirm-title"
    }, app.translator.trans('jrh-forum-commands.forum.nominate.confirm_title', {
      game: game[TITLE]
    })), m("p", {
      className: "jrh-Confirm-meta"
    }, [game[CONSOLE], m("a", {
      href: "https://retroachievements.org/game/".concat(game[ID]),
      target: "_blank",
      rel: "noopener noreferrer"
    }, app.translator.trans('jrh-forum-commands.forum.nominate.ra_page')), hoursLabel].filter(Boolean).map((part, i) => i ? [' • ', part] : part)), m("div", {
      className: "Form-group jrh-CommandModal-modes"
    }, m(Button, {
      className: "Button",
      onclick: () => this.picked = null
    }, app.translator.trans('jrh-forum-commands.forum.nominate.back')), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.insertNominate(game[ID])
    }, app.translator.trans('jrh-forum-commands.forum.nominate.confirm'))));
  }
  manualView() {
    return [m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-forum-commands.forum.nominate.url_label')), m("input", {
      className: "FormControl",
      type: "text",
      autocomplete: "off",
      placeholder: "https://retroachievements.org/game/1234",
      value: this.query,
      oninput: e => {
        this.query = e.target.value;
        this.error = null;
      }
    }), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-forum-commands.forum.nominate.url_help'), ' ', m("a", {
      href: "https://retroachievements.org/searchresults.php",
      target: "_blank",
      rel: "noopener noreferrer"
    }, app.translator.trans('jrh-forum-commands.forum.nominate.url_find')))), this.error ? m("p", {
      className: "helpText jrh-CommandModal-error"
    }, this.error) : null, m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      onclick: () => this.insertManual()
    }, app.translator.trans('jrh-forum-commands.forum.nominate.submit')))];
  }
  insertNominate(gameId) {
    this.attrs.oninsert("/nominate ".concat(this.event, " https://retroachievements.org/game/").concat(gameId));
    this.hide();
  }
  insertCancel(event) {
    this.attrs.oninsert("/cancel_nomination ".concat(event));
    this.hide();
  }
  insertManual() {
    const raw = (this.query || '').trim();
    const match = raw.match(/retroachievements\.org\/game\/(\d+)/i);
    const id = match ? match[1] : /^\d+$/.test(raw) ? raw : null;
    if (!id) {
      this.error = app.translator.trans('jrh-forum-commands.forum.nominate.url_invalid');
      return;
    }
    this.insertNominate(id);
  }
}
flarum.reg.add('jrh-forum-commands', 'forum/components/NominateModal', NominateModal);